import { CommandExecutionContext, SelfContainedCommand } from '../../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../../types/standardToolResults';
import { createFileLogger } from '../../../utils/logger';
import { wordPressAdapter } from '../../../services/integrations/adapters/WordPressAdapter';
import { resolveWordPressIntegration } from '../../../services/integrations/utils/integrationResolver';

const log = createFileLogger('WORDPRESS_PUBLISH');

export const wordPressPublishCommand: SelfContainedCommand = {
  id: 'wordpress_publish',
  description: `Publish AI-generated articles to your WordPress site on autopilot.

**Features:**
- ✅ Gutenberg block compatibility
- ✅ Custom post types support
- ✅ Category & tag management (auto-creates if needed)
- ✅ Automatic media uploads (featured images, inline images)
- ✅ Custom taxonomies support
- ✅ Meta fields support

**Parameters:**
- content: Article content (HTML or Gutenberg blocks)
- title: Post title (required)
- excerpt: Post excerpt/summary (optional)
- status: Post status - 'publish', 'draft', 'pending', 'private', 'future' (default: 'publish')
- postType: Post type - 'post', 'page', or custom post type (default: 'post')
- categories: Array of category names or IDs (auto-creates if needed)
- tags: Array of tag names (auto-creates if needed)
- featuredImage: URL or entity ID of featured image (will be uploaded)
- meta: Custom meta fields (object)
- slug: Custom post slug (optional)
- date: Publish date in ISO format (optional)

**Content Formats:**
- HTML: Standard HTML content (will be converted to Gutenberg blocks)
- Gutenberg: JSON block format (direct Gutenberg support)
- Markdown: Markdown content (will be converted to HTML then blocks)

**Example:**
\`\`\`
{
  "title": "My Article",
  "content": "<p>Article content...</p>",
  "categories": ["Technology", "AI"],
  "tags": ["wordpress", "automation"],
  "featuredImage": "https://example.com/image.jpg",
  "status": "publish"
}
\`\`\``,
  
  autonomyLevel: 'ask',
  visualTreatment: 'demonstrative',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Post title',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Title cannot be empty';
          return null;
        }
      },
      {
        name: 'content',
        type: 'string',
        description: 'Post content (HTML, Markdown, or Gutenberg JSON blocks)',
        required: true,
        validator: (value: string) => {
          if (!value || value.trim().length === 0) return 'Content cannot be empty';
          return null;
        }
      },
      {
        name: 'excerpt',
        type: 'string',
        description: 'Post excerpt/summary',
        required: false
      },
      {
        name: 'status',
        type: 'string',
        description: 'Post status: publish, draft, pending, private, or future',
        required: false,
        defaultValue: 'publish',
        validator: (value: string) => {
          const validStatuses = ['publish', 'draft', 'pending', 'private', 'future'];
          if (value && !validStatuses.includes(value)) {
            return `Status must be one of: ${validStatuses.join(', ')}`;
          }
          return null;
        }
      },
      {
        name: 'postType',
        type: 'string',
        description: 'Post type (post, page, or custom post type)',
        required: false,
        defaultValue: 'post'
      },
      {
        name: 'categories',
        type: 'array',
        description: 'Array of category names or IDs (auto-creates if needed)',
        required: false
      },
      {
        name: 'tags',
        type: 'array',
        description: 'Array of tag names (auto-creates if needed)',
        required: false
      },
      {
        name: 'featuredImage',
        type: 'string',
        description: 'URL or entity ID of featured image (will be uploaded to WordPress)',
        required: false
      },
      {
        name: 'meta',
        type: 'object',
        description: 'Custom meta fields (key-value pairs)',
        required: false
      },
      {
        name: 'slug',
        type: 'string',
        description: 'Custom post slug (optional, auto-generated from title if not provided)',
        required: false
      },
      {
        name: 'date',
        type: 'string',
        description: 'Publish date in ISO format (e.g., 2024-01-15T10:00:00Z)',
        required: false
      },
      {
        name: 'integrationId',
        type: 'string',
        description: 'WordPress integration ID (auto-detected if omitted)',
        required: false
      },
      {
        name: 'entityId',
        type: 'string',
        description: 'Entity ID of document to publish (will extract content if provided)',
        required: false
      }
    ],
    contextRequirements: {
      requiresEntity: false
    }
  },
  
  examples: {
    good: [
      {
        input: {
          title: 'Getting Started with AI',
          content: '<p>This is a comprehensive guide...</p>',
          categories: ['Technology', 'AI'],
          tags: ['tutorial', 'ai'],
          status: 'publish'
        },
        expectedOutput: createSuccessResult('Successfully published post to WordPress', {
          post_id: 123,
          post_url: 'https://example.com/getting-started-with-ai',
          status: 'publish'
        }),
        description: 'Publish a blog post with categories and tags',
        context: 'When you have an article ready to publish'
      },
      {
        input: {
          title: 'My Draft Article',
          content: '<p>Draft content...</p>',
          status: 'draft',
          postType: 'post'
        },
        expectedOutput: createSuccessResult('Successfully created draft post', {
          post_id: 124,
          post_url: 'https://example.com/wp-admin/post.php?post=124&action=edit',
          status: 'draft'
        }),
        description: 'Create a draft post for later review',
        context: 'When you want to save a draft'
      }
    ],
    bad: [
      {
        input: { title: '', content: '<p>Content</p>' },
        error: 'Title cannot be empty',
        why: 'Title is required',
        fix: 'Provide a valid title'
      },
      {
        input: { title: 'My Post', content: '' },
        error: 'Content cannot be empty',
        why: 'Content is required',
        fix: 'Provide post content'
      }
    ]
  },
  
  performance: {
    expectedDuration: 5000,
    complexity: 'high'
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    
    try {
      let { 
        title, 
        content, 
        excerpt, 
        status = 'publish',
        postType = 'post',
        categories = [],
        tags = [],
        featuredImage,
        meta = {},
        slug,
        date,
        integrationId,
        entityId
      } = params;
      
      // Validate required fields
      if (!title || title.trim().length === 0) {
        return createErrorResult('Missing title', 'title parameter is required', 'validation');
      }
      
      if (!content || content.trim().length === 0) {
        return createErrorResult('Missing content', 'content parameter is required', 'validation');
      }
      
      // If entityId is provided, try to extract content from entity
      if (entityId && (!content || content.trim().length === 0)) {
        try {
          log.log('[wordpress_publish] Extracting content from entity:', entityId);
          const useEntityStore = (await import('../../../stores/entityStore')).default;
          const entity = useEntityStore.getState().entities.find((e: any) => e._id === entityId || e.id === entityId);
          
          if (entity) {
            // Try to get content from entity skeleton
            if (entity.skeleton?.content) {
              content = entity.skeleton.content;
              log.log('[wordpress_publish] Extracted content from entity skeleton');
            } else if (entity.skeleton?.markdown) {
              content = entity.skeleton.markdown;
              log.log('[wordpress_publish] Extracted markdown from entity skeleton');
            }
            
            // Use entity name as title if title not provided
            if (!title && entity.name) {
              title = entity.name;
            }
          }
        } catch (error) {
          log.warn('[wordpress_publish] Could not extract content from entity:', error);
        }
      }
      
      log.log('[wordpress_publish] Publishing to WordPress:', {
        title: title.substring(0, 50),
        contentLength: content.length,
        postType,
        status,
        categoryCount: categories.length,
        tagCount: tags.length,
        hasFeaturedImage: !!featuredImage,
        hasIntegrationId: !!integrationId
      });
      
      // Resolve integration ID if not provided
      let resolvedIntegrationId = integrationId;
      if (!resolvedIntegrationId) {
        try {
          resolvedIntegrationId = await resolveWordPressIntegration(context.userId);
          log.log('[wordpress_publish] Auto-resolved integration ID:', resolvedIntegrationId);
        } catch (error) {
          return createErrorResult(
            'No WordPress integration found',
            error instanceof Error ? error.message : 'Please connect WordPress in Settings → Integrations',
            'validation'
          );
        }
      }
      
      // Upload featured image if provided
      let featuredMediaId: number | undefined;
      if (featuredImage) {
        try {
          log.log('[wordpress_publish] Uploading featured image:', featuredImage);
          
          // If it's an entity ID, try to get the file URL
          let imageUrl = featuredImage;
          if (featuredImage.includes('_') || featuredImage.length === 24) {
            try {
              const useEntityStore = (await import('../../../stores/entityStore')).default;
              const entity = useEntityStore.getState().entities.find((e: any) => 
                e._id === featuredImage || e.id === featuredImage || e.llmId === featuredImage
              );
              
              if (entity?.skeleton?.url || entity?.skeleton?.fileUrl) {
                imageUrl = entity.skeleton.url || entity.skeleton.fileUrl;
                log.log('[wordpress_publish] Resolved entity to image URL');
              }
            } catch (error) {
              log.warn('[wordpress_publish] Could not resolve entity ID, using as URL:', error);
            }
          }
          
          const media = await wordPressAdapter.uploadMedia(
            resolvedIntegrationId,
            imageUrl,
            `featured-${Date.now()}.jpg`,
            title
          );
          featuredMediaId = media.id;
          log.log('[wordpress_publish] Featured image uploaded:', featuredMediaId);
        } catch (error) {
          log.warn('[wordpress_publish] Failed to upload featured image, continuing without it:', error);
        }
      }
      
      // Process categories - get or create each one
      const categoryIds: number[] = [];
      const createdCategories: string[] = [];
      
      for (const category of categories) {
        try {
          if (typeof category === 'number') {
            categoryIds.push(category);
          } else {
            const categoryObj = await wordPressAdapter.getOrCreateCategory(
              resolvedIntegrationId,
              category
            );
            categoryIds.push(categoryObj.id);
            if (!categories.includes(categoryObj.name)) {
              createdCategories.push(categoryObj.name);
            }
          }
        } catch (error) {
          log.warn('[wordpress_publish] Failed to get/create category:', category, error);
        }
      }
      
      // Process tags - get or create each one
      const tagIds: number[] = [];
      const createdTags: string[] = [];
      
      for (const tag of tags) {
        try {
          if (typeof tag === 'number') {
            tagIds.push(tag);
          } else {
            const tagObj = await wordPressAdapter.getOrCreateTag(
              resolvedIntegrationId,
              tag
            );
            tagIds.push(tagObj.id);
            if (!tags.includes(tagObj.name)) {
              createdTags.push(tagObj.name);
            }
          }
        } catch (error) {
          log.warn('[wordpress_publish] Failed to get/create tag:', tag, error);
        }
      }
      
      // Publish post
      const result = await wordPressAdapter.publishPost(resolvedIntegrationId, {
        title,
        content,
        excerpt,
        status: status as any,
        postType,
        categories: categoryIds,
        tags: tagIds,
        featuredMedia: featuredMediaId,
        meta,
        slug,
        date
      });
      
      log.log('[wordpress_publish] Post published successfully:', result.post_id);
      
      // Stream progress update
      if (context.onProgress) {
        context.onProgress({
          type: 'streaming_update',
          message: `Published to WordPress: ${title}`,
          post_id: result.post_id,
          post_url: result.post_url
        });
      }
      
      return createSuccessResult(
        `Successfully published "${title}" to WordPress`,
        {
          post_id: result.post_id,
          post_url: result.post_url,
          status: result.status,
          title,
          categories: categories,
          tags: tags,
          media_uploaded: result.media_uploaded || [],
          categories_created: createdCategories,
          tags_created: createdTags
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime,
            method: 'wordpress_publish',
            affectedEntities: entityId ? [entityId] : []
          } as any,
          ui: {
            notify: true,
            showInResult: true,
            resultPreview: 'full'
          }
        }
      );
      
    } catch (error) {
      log.error('[wordpress_publish] Error publishing to WordPress:', error);
      return createErrorResult(
        'Failed to publish to WordPress',
        error instanceof Error ? error.message : 'Unknown error',
        'execution',
        { metadata: { executionTime: Date.now() - startTime } }
      );
    }
  },
  
  formatViewData: (result: StandardToolResult, toolInput: any) => {
    if (result.status === 'success' && result.data) {
      return {
        type: 'wordpress_publish',
        message: result.message,
        data: {
          post_id: result.data.post_id,
          post_url: result.data.post_url,
          title: result.data.title,
          status: result.data.status,
          categories: result.data.categories || [],
          tags: result.data.tags || [],
          media_uploaded: result.data.media_uploaded || []
        }
      };
    }
    return {
      type: 'wordpress_publish',
      message: result.message,
      data: result.data
    };
  }
};

